import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PenFilledIcon from '../../../../../../../../../../../../assets/icons/PenFilled';
import { IManualInputMaterialData } from '../../../../../../../../../../../../components/dialog-manager/dialogs/enter-manual-input-material-dialog/EnterManualInputMaterialDialog';
import ErrorMessage from '../../../../../../../../../../../../components/error-message';
import useDialogContext from '../../../../../../../../../../../../hooks/dialog/useDialogContext';
import CustomIcon from '../../../../../../../../../../../../shared/components/custom-icon/CustomIcon';
import { IComponent } from '../../../../../../../../../../../../shared/interfaces/IComponent';
import { PROCESS_DROPDOWN_OPTIONS, ProcessIdsMap } from '../../../../PcfWizard.type';
import InputMaterialWasteManagementForm from './components/input-material-waste-management-form/InputMaterialWasteManagementForm';
import SelectedInputMaterialDetail from './components/selected-input-material-detail/SelectedInputMaterialDetail';

interface InputMaterialProps {
  formPath: string;
  processIndex: number;
  processType: PROCESS_DROPDOWN_OPTIONS;
  onRemoveInputMaterialCard?: () => void;
}

const InputMaterial = ({
  formPath,
  processIndex,
  processType,
  onRemoveInputMaterialCard,
}: InputMaterialProps): JSX.Element => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();

  const { control, setValue, getValues } = useFormContext();
  const {
    field: { ref, onChange, value },
    fieldState: { error },
  } = useController({
    name: formPath,
    control,
  });

  const handleComponentSelect = (component: IComponent) => {
    /* eslint-disable camelcase */
    onChange({
      id: component.productId,
      material_name: component.productName,
      product_cid: component.productCid,
      pcf_excluding_biogenic: component.pcfExcluding,
      pcf_including_biogenic: component.pcfIncluding,
      declared_unit: component.declaredUnit,
      unitary_product_amount: component.unitaryProductAmount,
    });
    /* eslint-enable camelcase */
  };

  const handleManualAdd = (manualInputMaterialData: IManualInputMaterialData) => {
    /* eslint-disable camelcase */
    onChange({
      material_name: manualInputMaterialData.materialName,
      fossil_ghg_emissions: manualInputMaterialData.fossilGhgEmissions,
      pcf_excluding_biogenic: manualInputMaterialData.pcfExcluding,
      pcf_including_biogenic: manualInputMaterialData.pcfIncluding,
      unitary_product_amount: manualInputMaterialData.declaredAmount,
      declared_unit: manualInputMaterialData.declaredUnit,
      source: manualInputMaterialData.source,
      comment: manualInputMaterialData.comment,
      quality: 'GOOD', // TODO: This should be set by the button in the dialog
    });
    /* eslint-enable camelcase */
  };

  const onRemoveInputMaterial = useCallback(() => {
    // Remove input material from formData
    onChange(null);

    if (
      getValues(`processes[${processIndex}].process_id`) ===
      ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]
    ) {
      /* eslint-disable camelcase */
      // Reset waste management when input material is removed in Individual process (single selection for now).
      // Use setValue instead of resetField because resetField restores the initial default,
      // which may contain stale calculation data when copying a calculation.
      setValue(`processes[${processIndex}].waste_management`, {
        amount_per_piece: null,
        waste_per_piece: null,
        waste_treatment: null,
      });
      /* eslint-enable camelcase */
    }
  }, [onChange, getValues, processIndex, setValue]);

  const handleRemoveCard = useCallback(() => {
    // Remove input material
    onRemoveInputMaterial();

    // Call parent callback if provided
    onRemoveInputMaterialCard?.();
  }, [onRemoveInputMaterial, onRemoveInputMaterialCard]);

  return (
    <div className='surface-ground border-round-2xl p-3 mb-3'>
      <div className='flex flex-row w-full justify-content-between align-items-center'>
        <h3 className='mb-3 font-medium' style={{ fontSize: '16px' }}>
          {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.title')}
        </h3>
        {processType === PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS && (
          <Button
            icon='pi pi-times'
            className='p-button-text p-button-plain p-button-rounded p-button-sm'
            onClick={handleRemoveCard}
            tooltip={t(
              'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.removeInputMaterialText',
            )}
            tooltipOptions={{ position: 'left' }}
          />
        )}
      </div>
      {!value ? (
        // If nothing is selected, show buttons to select or enter manually
        <div>
          <div className='flex align-items-center gap-4'>
            {/* Select Component button */}
            <Button
              label={t(
                'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.selectComponentOrText',
              )}
              className='p-button-primary'
              icon='pi pi-plus-circle'
              iconPos='left'
              pt={{
                icon: {
                  className: 'text-xl',
                },
              }}
              onClick={() => {
                openDialog('select-component-for-calculation-dialog', {
                  onSelect: handleComponentSelect,
                });
              }}
            />

            <span className='font-sm text-gray-400'>
              {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.orText')}
            </span>

            {/* Enter Manually button */}
            <div
              className='p-button p-0 p-button-text flex flex-row align-items-center'
              onClick={() => {
                openDialog('enter-manual-input-material-dialog', { onAdd: handleManualAdd });
              }}
            >
              <CustomIcon icon={PenFilledIcon} className='flex w-10 p-0 h-full' />
              <span className='text-primary font-medium text-center h-full'>
                {t(
                  'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.enterManuallyText',
                )}
              </span>
            </div>
          </div>
          <div>
            {/* Error Message Block for Input Materials Required Error Message */}
            {error?.message && (
              <div className='py-2'>
                <ErrorMessage message={error.message} />
              </div>
            )}
          </div>
        </div>
      ) : (
        // If a component is selected, show their details
        <div className='p-3 border-round-2xl bg-dark-gray'>
          <SelectedInputMaterialDetail
            onRemoveInputMaterial={onRemoveInputMaterial}
            inputMaterial={value}
          />

          {/* Waste Management Section is shown inside InputMaterial for Individual Process */}
          {processType === PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS && (
            <>
              <Divider layout='horizontal' />
              <InputMaterialWasteManagementForm processIndex={processIndex} />
            </>
          )}
        </div>
      )}

      {/* Hidden input to enable focus & auto-scroll on error */}
      <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={ref} />
    </div>
  );
};

export default InputMaterial;
